import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import parse from 'html-react-parser';
import CircularProgress from '@mui/material/CircularProgress';
import { City } from '../../../interfaces/config';
import { ErrorMessage } from '../../Forms/ErrorMessage';
import { __t } from '../../../utils/t';
import { highlightMatchingText } from '../../../utils';
import { appActions } from '../../../redux/slices/appSlice';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { Loading } from '../../../enums';
import { CityAutocompleteProps } from '..';

import './styles.scss';

export const CityAutocompleteDesktop = ({
    options,
    selectedOption,
    errors,
    tabIndex,
    onSearch,
    onSelect,
    setSearchOptions,
}: CityAutocompleteProps) => {
    const dispatch = useAppDispatch();
    const isLoading = useAppSelector((state) => state.app.loading === Loading.CityAutocomplete);

    const [inputValue, setInputValue] = useState('');
    const [optionsSelectVisible, setOptionsSelectVisible] = useState(false);
    const [showOverlay, setShowOverlay] = useState(false);

    useEffect(() => {
        return function () {
            setSearchOptions(null);
        };
    }, []);

    useEffect(() => {
        if (selectedOption) {
            setInputValue(selectedOption.name);
            setSearchOptions([selectedOption]);
        }
    }, [selectedOption]);

    const inputChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.trimStart();
        onSelect(null);
        setSearchOptions(null);
        setInputValue(value);
        setOptionsSelectVisible(true);
        onSearch(value);
    };

    const onInputFocus = () => {
        setShowOverlay(true);

        if (!inputValue) {
            onSearch('');
        }

        setOptionsSelectVisible(true);
    };

    const optionSelectHandler = (option: City) => {
        onSelect(option);
        setOptionsSelectVisible(false);
        setShowOverlay(false);
    };

    const onOverlayClick = useCallback(() => {
        setOptionsSelectVisible(false);
        setShowOverlay(false);
        if (!selectedOption) {
            setInputValue('');
            setSearchOptions(null);
            dispatch(appActions.setLoading(null));
        }
    }, [selectedOption]);

    const loader = useMemo(
        () => (isLoading ? <CircularProgress size={16} className='app-city-autocomplete__loader' /> : null),
        [isLoading],
    );

    const renderOptions = useMemo(() => {
        if (!optionsSelectVisible || !options) {
            return null;
        }

        if (options && !options.length && !isLoading) {
            return (
                <ul className='app-city-autocomplete__options-list app-city-autocomplete__options-list--drop-up'>
                    <li className='app-city-autocomplete__option-item'>
                        <span>{__t('По вашему запросу ничего не найдено')}</span>
                    </li>
                </ul>
            );
        }

        return (
            <ul className='app-city-autocomplete__options-list app-city-autocomplete__options-list--drop-up'>
                {options.map((option) => (
                    <li
                        key={option.id}
                        className={`app-city-autocomplete__option-item ${
                            option.id === selectedOption?.id ? 'app-city-autocomplete__option-item--selected' : ''
                        }`}
                        onClick={() => optionSelectHandler(option)}
                    >
                        {parse(option.country.flag)}
                        {highlightMatchingText(option.name, inputValue)}
                        <span className='app-city-autocomplete__option-additional-text'>
                            {option.region ? `${option.region}, ` : ''}
                            {option.country.name}
                        </span>
                    </li>
                ))}
            </ul>
        );
    }, [options, selectedOption, optionsSelectVisible, inputValue, isLoading]);

    const renderOverlay = useMemo(() => {
        if (showOverlay) {
            return <div className='app-city-autocomplete__overlay' onClick={onOverlayClick}></div>;
        }
    }, [showOverlay, selectedOption]);

    return (
        <>
            <div className='app-city-autocomplete'>
                <div className='app-city-autocomplete__input-container'>
                    <input
                        type='text'
                        value={inputValue}
                        onChange={inputChangeHandler}
                        className={`app-city-autocomplete__input-component ${
                            errors?.length ? 'app-city-autocomplete__input-component--has-error' : ''
                        }`}
                        placeholder={__t('Введите город')}
                        onFocus={onInputFocus}
                        autoComplete='off'
                        tabIndex={tabIndex}
                    />
                    {loader}
                </div>
                {renderOptions}
                {renderOverlay}
            </div>
            <ErrorMessage errors={errors} />
        </>
    );
};
